import { useState, useEffect } from 'react';
import { Flex, Icon, Tag, Text } from '@chakra-ui/react';
import FileUploadInput from 'common/components/file-upload-input';
import WrapInputLabel from 'common/components/wrap-input-label';
import ApplyButtons from 'components/apply-shared/apply-buttons';
import { useUploadRequestUrlMutation } from 'redux/api/ciosUsersApi/ciosUsersApi';
import { useActivatePlanMutation } from 'redux/api/ciosUsersApi/ciosUsersApi';
import { toast } from 'react-toastify';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import { uploadFileToS3 } from 'utils/uploadS3';
import { extractErrorMsg } from 'utils/errors';
import { HiCheckBadge } from 'react-icons/hi2';
import ApplyTitle from 'components/apply-shared/apply-title';
import ApplyContent from 'components/apply-shared/apply-content';
import { ApplyFormField, ApplyFormLayout } from 'common/layouts/apply-form';
import { documentStatusEnum, backgroundTypeEnum } from 'common/utils/enums';

// Define this array outside the component so its reference is stable across re-renders.
const DOCUMENT_UPDATED_STATUSES = [
  documentStatusEnum.VERIFIED,
  documentStatusEnum.UPLOADED,
];

const PlanStepActivatePlan = ({
  stepNumber,
  onBackClick,
  onNextClick,
  planData,
  ...args
}) => {
  // User tracker
  const {
    trackLink,
    trackPageLoad,
    trackCustomEvent,
    trackFormSubmission,
    trackFormSubmissionSuccess,
    trackFormSubmissionError,
  } = useUserTracker();
  useEffect(() => {
    trackPageLoad('activatePlanLeaseAndVerification');
  }, []);

  // Set up activate hook
  const [activatePlan, { isLoading: isActivateLoading }] =
    useActivatePlanMutation();

  // Document update states
  const [idDoc, setIDDoc] = useState({
    isLoading: false,
    isSuccess: false,
    isError: false,
    url: '',
  });
  const [leaseDoc, setLeaseDoc] = useState({
    isLoading: false,
    isSuccess: false,
    isError: false,
    url: '',
  });
  const [uploadRequestUrl] = useUploadRequestUrlMutation();

  // Check for previous lease doc
  useEffect(() => {
    if (DOCUMENT_UPDATED_STATUSES.includes(planData?.leaseVerification)) {
      setLeaseDoc((old) => ({
        ...old,
        isLoading: false,
        isSuccess: true,
        isError: false,
        uploaded: true,
        url: planData?.lease?.document,
      }));
    }

    if (DOCUMENT_UPDATED_STATUSES.includes(planData?.idVerification)) {
      setIDDoc((old) => ({
        ...old,
        isLoading: false,
        isSuccess: true,
        isError: false,
      }));
    }
  }, [planData]);

  const brand = planData?.property?.brand;
  const isDarkBg = brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK;

  return (
    <div className='flex flex-col w-full h-full'>
      <ApplyTitle
        title='Activate Plan'
        subtitle='Upload your lease agreement and a goverment-issued photo ID'
        buttons={
          <ApplyButtons
            moveOn={true}
            nextTitle='Activate'
            isValid={idDoc.isSuccess && leaseDoc.isSuccess}
            onNextClick={() => {
              let submitValues = {
                planId: planData?.id,
              };

              // Ensure that leaseDoc and idDoc aren't overwritten
              leaseDoc?.url && (submitValues.leaseDocUrl = leaseDoc.url);
              idDoc?.url && (submitValues.idUrl = idDoc.url);

              trackFormSubmission(submitValues);
              activatePlan(submitValues)
                .unwrap()
                .then((d) => {
                  trackFormSubmissionSuccess(submitValues);

                  if (leaseDoc.isSuccess && idDoc.isSuccess) {
                    onNextClick();
                  }
                })
                .catch((e) => {
                  const errMsg = extractErrorMsg(e, 'Update Failed');
                  toast.error(errMsg);
                  trackFormSubmissionError(submitValues, errMsg);
                });
            }}
            onBackClick={onBackClick}
            disableBack={!onBackClick}
          />
        }
      />

      {/* main content */}
      <ApplyContent>
        {planData?.property && (
          <ApplyFormLayout
            className={`p-6 rounded-lg border relative overflow-clip ${
              brand ? 'border-gray-200' : 'border-gray-200 bg-gray-50'
            }`}
            style={brand?.bgColor ? { backgroundColor: brand.bgColor } : {}}
          >
            <Flex className='z-10 relative flex-col sm:flex-row gap-6 sm:gap-8 col-span-2'>
              {brand && (
                <img
                  src={isDarkBg && brand?.logoAlt ? brand?.logo : brand?.logo}
                  alt={planData?.property?.name}
                  className='max-w-[140px] max-h-[50px] object-contain'
                />
              )}
              <Flex className='flex-col gap-1'>
                <Text
                  className={`font-medium text-lg ${
                    isDarkBg ? 'text-white' : 'text-black'
                  }`}
                >
                  ReLease Coverage at {planData?.property?.name}
                </Text>
                <Text
                  className={`text-base ${isDarkBg ? 'text-white opacity-60' : 'text-black opacity-60'}`}
                >
                  {planData?.property?.address?.streetAddress1},{' '}
                  {planData?.property?.address?.city},{' '}
                  {planData?.property?.address?.state}{' '}
                  {planData?.property?.address?.postalCode}
                </Text>
              </Flex>
            </Flex>
            {brand?.backgroundHeroImage && (
              <img
                src={brand?.backgroundHeroImage}
                alt={planData?.property?.name}
                className='absolute inset-0 opacity-15 z-0 object-cover'
              />
            )}
          </ApplyFormLayout>
        )}
        {/* Lease Document is conditional on not being uploaded previously */}
        <Flex className='flex-col w-full gap-6 p-6 border border-gray-200 rounded-md'>
          {/* title */}
          <Flex className='flex-row gap-3 w-full justify-between'>
            <h2 className='text-lg font-medium'>
              Lease Agreement for{' '}
              {planData?.property?.name ||
                planData?.lease?.address?.streetAddress1}
            </h2>
            {leaseDoc.isSuccess ? (
              <Tag
                className='ml-4 pl-2 pr-3 gap-2 rounded-full text-sm'
                colorScheme='green'
              >
                <Icon as={HiCheckBadge} />
                Uploaded
              </Tag>
            ) : null}
          </Flex>

          {!leaseDoc.isSuccess && (
            <>
              <Text className='text-base text-gray-500'>
                Upload the full PDF copy of your signed lease agreement for{' '}
                {planData?.property?.name ||
                  planData?.lease?.address?.streetAddress1}
                . Please make sure all pages are included in the file.
              </Text>
              <ApplyFormField>
                <WrapInputLabel isRequired>
                  Upload Your Lease Agreement
                </WrapInputLabel>

                {/* file upload input */}
                <FileUploadInput
                  disableUpload={leaseDoc.isLoading}
                  isUploadError={leaseDoc.isError}
                  isUploadLoading={leaseDoc.isLoading}
                  isUploadSuccess={leaseDoc.isSuccess}
                  handlePDFClear={() => {
                    setLeaseDoc((old) => ({
                      ...old,
                      isLoading: false,
                      isSuccess: false,
                      isError: false,
                      url: '',
                    }));
                  }}
                  handlePDFUpload={async (file) => {
                    let uploadUrlReq;
                    let fileTrack = { fileName: file.name, size: file.size };
                    setLeaseDoc((old) => ({
                      ...old,
                      isLoading: true,
                      fileName: file.name,
                    }));

                    // Get the signing url for uploading to s3
                    try {
                      trackCustomEvent('fileUploadSelection', {
                        name: 'leaseDocUrl',
                        file: fileTrack,
                      });
                      trackCustomEvent('s3UrlUploadRequest', {
                        name: 'leaseDocUrl',
                        file: fileTrack,
                      });
                      uploadUrlReq = await uploadRequestUrl({
                        fileName: file.name,
                        contentType: file.type,
                      }).unwrap();
                    } catch (e) {
                      setLeaseDoc((old) => ({
                        ...old,
                        isLoading: false,
                        isError: true,
                        fileName: file.name,
                      }));
                      const errMsg = extractErrorMsg(
                        e,
                        'failed to upload lease document'
                      );

                      toast.error(errMsg);
                      trackCustomEvent('s3UrlUploadRequestError', {
                        name: 'leaseDocUrl',
                        file: fileTrack,
                        error: errMsg,
                      });
                      return;
                    }

                    if (
                      !uploadUrlReq?.data?.uploadUrl ||
                      !uploadUrlReq?.data?.viewUrl
                    ) {
                      setLeaseDoc((old) => ({
                        ...old,
                        isLoading: false,
                        isError: true,
                        fileName: file.name,
                      }));
                      toast.error('failed to upload lease document');
                      trackCustomEvent('s3UrlUploadRequestError', {
                        name: 'leaseDocUrl',
                        file: fileTrack,
                        error: 'Improper s3 url return value',
                      });
                    }

                    // Upload the file to s3
                    try {
                      trackCustomEvent('s3FileUploadRequest', {
                        name: 'leaseDocUrl',
                        file: fileTrack,
                        s3Url: uploadUrlReq?.data,
                      });
                      await uploadFileToS3({
                        s3Url: uploadUrlReq?.data?.uploadUrl,
                        file,
                      });

                      trackCustomEvent('s3FileUploadSuccess', {
                        name: 'leaseDocUrl',
                        file: fileTrack,
                        s3Url: uploadUrlReq?.data,
                      });

                      // Have to set the uploaded file url on the lease document
                      setLeaseDoc((old) => ({
                        ...old,
                        url: uploadUrlReq?.data?.viewUrl,
                        fileName: file.name,
                        isLoading: false,
                        isError: false,
                        isSuccess: true,
                      }));

                      toast.info(
                        uploadUrlReq?.status ||
                          'lease has been uploaded successfully'
                      );
                    } catch (e) {
                      setLeaseDoc((old) => ({
                        ...old,
                        fileName: file.name,
                        isLoading: false,
                        isError: true,
                        isSuccess: false,
                      }));
                      const errMsg = extractErrorMsg(
                        e,
                        'failed to upload lease document'
                      );
                      toast.error(errMsg);
                      trackCustomEvent('s3FileUploadError', {
                        name: 'leaseDocUrl',
                        file: fileTrack,
                        s3Url: uploadUrlReq?.data,
                        error: errMsg,
                      });
                      return;
                    }
                  }}
                />
              </ApplyFormField>
            </>
          )}
        </Flex>

        {/* Identity Verification */}
        <Flex className='flex-col w-full gap-6 p-6 border border-gray-200 rounded-md'>
          {/* title */}
          <Flex className='flex-row gap-3 w-full justify-between'>
            <h2 className='text-lg font-medium'>Government-Issued Photo ID</h2>
            {idDoc.isSuccess && (
              <Tag
                className='ml-4 pl-2 pr-3 gap-2 rounded-full text-sm'
                colorScheme='green'
              >
                <Icon as={HiCheckBadge} />
                Uploaded
              </Tag>
            )}
          </Flex>

          {!idDoc.isSuccess ? (
            <>
              <Text className='text-base text-gray-500'>
                Upload a copy or photo of a valid government-issued photo ID,
                such as a driver’s license, passport, or citizenship card.
                Please ensure the ID is clear and all details are visible.
              </Text>
              <ApplyFormField>
                <WrapInputLabel isRequired>Upload Your Photo ID</WrapInputLabel>

                {/* file upload input */}
                <FileUploadInput
                  disableUpload={idDoc.isLoading}
                  isUploadError={idDoc.isError}
                  isUploadLoading={idDoc.isLoading}
                  isUploadSuccess={idDoc.isSuccess}
                  handlePDFClear={() => {
                    setIDDoc((old) => ({
                      ...old,
                      isLoading: false,
                      isSuccess: false,
                      isError: false,
                      url: '',
                    }));
                  }}
                  handlePDFUpload={async (file) => {
                    let uploadUrlReq;
                    setIDDoc((old) => ({ ...old, isLoading: true }));
                    let fileTrack = { fileName: file.name, size: file.size };

                    // Get the signing url for uploading to s3
                    try {
                      trackCustomEvent('fileUploadSelection', {
                        name: 'idUrl',
                        file: fileTrack,
                      });
                      trackCustomEvent('s3UrlUploadRequest', {
                        name: 'idUrl',
                        file: fileTrack,
                      });
                      uploadUrlReq = await uploadRequestUrl({
                        fileName: file.name,
                        contentType: file.type,
                      }).unwrap();
                    } catch (e) {
                      const errMsg =
                        e?.msg ||
                        e?.data?.msg ||
                        e?.message ||
                        'failed to upload ID document';
                      setIDDoc((old) => ({
                        ...old,
                        isLoading: false,
                        fileName: file.name,
                        isError: true,
                      }));
                      toast.error(errMsg);
                      trackCustomEvent('s3UrlUploadRequestError', {
                        name: 'idUrl',
                        file: fileTrack,
                        error: errMsg,
                      });
                      return;
                    }

                    if (
                      !uploadUrlReq?.data?.uploadUrl ||
                      !uploadUrlReq?.data?.viewUrl
                    ) {
                      setIDDoc((old) => ({
                        ...old,
                        isLoading: false,
                        fileName: file.name,
                        isError: true,
                      }));
                      toast.error('failed to upload ID document');
                    }

                    // Upload the file to s3

                    try {
                      trackCustomEvent('s3FileUploadRequest', {
                        name: 'idUrl',
                        file: fileTrack,
                        s3Url: uploadUrlReq?.data,
                      });
                      await uploadFileToS3({
                        s3Url: uploadUrlReq?.data?.uploadUrl,
                        file,
                      });
                      trackCustomEvent('s3FileUploadSuccess', {
                        name: 'idUrl',
                        file: fileTrack,
                        s3Url: uploadUrlReq?.data,
                      });

                      // Have to set the uploaded file url on the ID document
                      setIDDoc((old) => ({
                        ...old,
                        url: uploadUrlReq?.data?.viewUrl,
                        fileName: file.name,
                        isLoading: false,
                        isError: false,
                        isSuccess: true,
                      }));

                      toast.info(
                        uploadUrlReq?.status ||
                          'ID has been uploaded successfully'
                      );
                    } catch (e) {
                      setIDDoc((old) => ({
                        ...old,
                        fileName: file.name,
                        isLoading: false,
                        isError: true,
                        isSuccess: false,
                      }));
                      const errMsg = extractErrorMsg(
                        e,
                        'failed to upload ID document'
                      );
                      toast.error(errMsg);
                      trackCustomEvent('s3FileUploadError', {
                        name: 'idUrl',
                        file: fileTrack,
                        s3Url: uploadUrlReq?.data,
                        error: errMsg,
                      });
                      return;
                    }
                  }}
                />
              </ApplyFormField>
            </>
          ) : null}
        </Flex>
      </ApplyContent>
    </div>
  );
};

export default PlanStepActivatePlan;
